import { useMemo, useState } from "react";
import { paymentMethodLabel } from "@/lib/sellerCheckout";
import { Link } from "wouter";
import { CheckCircle2, Clock, ShoppingBag, Search, XCircle } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { MetricCard } from "@/components/dashboard/MetricCard";
import { ORDER_STATUS_LABEL, OrderStatusBadge } from "@/components/dashboard/OrderStatusBadge";
import { Card, CardContent } from "@/components/ui/card";
import { Pagination } from "@/components/ui/pagination";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ORDER_RANGES,
  useOrdersCount,
  useOrdersPage,
  useOrdersSummary,
  type OrderRange,
  type OrderStatusFilter,
} from "@/hooks/use-orders";
import { useSellers } from "@/hooks/use-sellers";
import { formatCurrency, formatDateTime, formatNumber, matches } from "@/lib/format";
import { ORDER_STATUSES } from "@/types";

type StatusFilter = OrderStatusFilter;

export default function Orders() {
  const [range, setRange] = useState<OrderRange>("7d");
  const [status, setStatus] = useState<StatusFilter>("todos");
  const [sellerId, setSellerId] = useState("todos");
  const [search, setSearch] = useState("");

  const sellers = useSellers();
  const summary = useOrdersSummary(range, sellerId);
  const count = useOrdersCount(range, status, sellerId);
  const page = useOrdersPage(range, status, sellerId, count.data);

  // O período, a situação e o estabelecimento entram na consulta; a busca por
  // texto filtra a página que já está na tela. Levá-la ao servidor exigiria um
  // índice de busca que o Firestore não tem, e refazer a consulta a cada tecla
  // seria uma leitura por caractere digitado.
  const rows = useMemo(() => {
    if (!search) return page.rows;
    return page.rows.filter(
      (order) =>
        matches(order.sellerName, search) ||
        matches(order.userName, search) ||
        matches(order.id, search),
    );
  }, [page.rows, search]);

  return (
    <>
      <PageHeader
        title="Pedidos"
        description="Visão global da plataforma, com corte por período, situação e estabelecimento."
        actions={
          <Select value={range} onValueChange={(v) => setRange(v as OrderRange)}>
            <SelectTrigger className="w-full sm:w-36" aria-label="Escolher o período">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(ORDER_RANGES).map(([key, value]) => (
                <SelectItem key={key} value={key}>
                  {value.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      <div className="mb-6 grid grid-cols-1 gap-3 xs:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Pedidos"
          value={formatNumber(summary.data?.total ?? 0)}
          hint={`No período de ${ORDER_RANGES[range].label.toLowerCase()}`}
          icon={ShoppingBag}
          loading={summary.isLoading}
        />
        <MetricCard
          label="Em aberto"
          value={formatNumber(summary.data?.openCount ?? 0)}
          hint="Ainda não entregues nem cancelados"
          icon={Clock}
          tone="warning"
          loading={summary.isLoading}
        />
        <MetricCard
          label="Entregues"
          value={formatNumber(summary.data?.deliveredCount ?? 0)}
          hint={formatCurrency(summary.data?.revenue ?? 0)}
          icon={CheckCircle2}
          tone="ok"
          loading={summary.isLoading}
        />
        <MetricCard
          label="Cancelados"
          value={formatNumber(summary.data?.cancelledCount ?? 0)}
          hint="No mesmo período"
          icon={XCircle}
          tone="error"
          loading={summary.isLoading}
        />
      </div>

      <div className="mb-4 flex flex-col gap-2 lg:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filtrar esta página por estabelecimento, cliente ou id"
            className="pl-9"
            aria-label="Buscar pedido"
          />
        </div>
        <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
          <SelectTrigger className="w-full lg:w-48" aria-label="Filtrar por situação">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todas as situações</SelectItem>
            <SelectItem value="abertos">Em aberto</SelectItem>
            {ORDER_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {ORDER_STATUS_LABEL[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sellerId} onValueChange={setSellerId}>
          <SelectTrigger className="w-full lg:w-60" aria-label="Filtrar por estabelecimento">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os estabelecimentos</SelectItem>
            {(sellers.data ?? []).map((seller) => (
              <SelectItem key={seller.id} value={seller.id}>
                {seller.name || "Sem nome"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0 sm:p-0">
          {page.isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={ShoppingBag}
              title="Nenhum pedido encontrado"
              description={
                search || status !== "todos" || sellerId !== "todos"
                  ? "Ajuste a busca ou os filtros para ver outros resultados."
                  : "Não houve pedido neste período. Escolha um período maior no topo da tela."
              }
            />
          ) : (
            <Table className="table-stacked">
              <TableHeader>
                <TableRow>
                  <TableHead>Estabelecimento</TableHead>
                  <TableHead className="hidden lg:table-cell">Cliente</TableHead>
                  <TableHead>Valor</TableHead>
                  <TableHead className="hidden md:table-cell">Criado em</TableHead>
                  <TableHead>Situação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell className="cell-title">
                      {order.sellerId ? (
                        <Link
                          href={`/vendedores/${order.sellerId}`}
                          className="font-semibold text-foreground hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          {order.sellerName || "Sem nome"}
                        </Link>
                      ) : (
                        <span className="font-semibold">{order.sellerName || "Sem nome"}</span>
                      )}
                      <p className="truncate text-caption text-muted-foreground">
                        {order.deliveryType === "pickup" ? "Retirada" : "Entrega"}
                        {order.paymentMethod ? `, ${paymentMethodLabel(order.paymentMethod)}` : ""}
                      </p>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {order.userId?.startsWith("guest_") ? (
                        <span className="text-muted-foreground">Convidado</span>
                      ) : order.userId ? (
                        <Link
                          href={`/clientes/${order.userId}`}
                          className="hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          {order.userName || "Sem nome"}
                        </Link>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell className="font-semibold tabular-nums">
                      {formatCurrency(order.total ?? 0)}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {formatDateTime(order.createdAt)}
                    </TableCell>
                    <TableCell>
                      <OrderStatusBadge status={order.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Pagination {...page.paginationProps} itemLabel="pedidos" />
        </CardContent>
      </Card>
    </>
  );
}
